import { Component, OnInit, Output, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import { CurrencyPipe, PercentPipe } from "@angular/common";
import { Account, AccountService } from "../../../../account/account";
import { AuthenticationService, EnvironmentConfig, Location, LocationPipe, LogService } from "../../../../core/core";
import { DropDownItem, CheckboxCard } from "../../../../forms/forms";
import { AzureSearchService, AzureSearchRequest } from "../../../../azure-search/azure-search";
import {
  CatalogService,
  InventoryItem,
  InventoryLocation,
  InventoryService,
  InventoryTypeService,
  MachineCatalog,
  MachineType,
} from "../../../inventory";
import { ToastrService } from "ngx-toastr";
import { catchError, debounceTime, distinctUntilChanged, EMPTY, forkJoin, map, mergeMap, Observable, of, OperatorFunction, tap } from "rxjs";
import { MeterKind, meterKindForNames, meterKindLabel, metricMeterUnit, standardMeterUnit } from "../../../usage-measurement";
import { StripeService } from "../../../../billing/services/stripe.service";
import { SelectPaymentMethodsComponent } from "../../../../billing/components/select-payment-methods/select-payment-methods.component";
import {
  MachineAnalysisResult,
  MachineTypeDefinitionOption,
  LocationBasedRentalRatesResult,
  OpenAIService,
} from "../../../../rubbl-shared/services/open-ai.service";

@Component({
  selector: "common-machine-create-wizard-details",
  templateUrl: "./machine-create-wizard-details.component.html",
  styleUrls: ["./machine-create-wizard-details.component.scss"],
  providers: [CurrencyPipe, PercentPipe],
})
export class MachineCreateWizardDetailsComponent implements OnInit {
  @Output() machineCreated = new EventEmitter<InventoryItem>();

  // Machine selection properties
  selectedMachineType: string = "";
  selectedMachineMake: string = "";
  selectedMachineModel: string = "";

  // Form data
  entity: InventoryItem = {
    location: new InventoryLocation(),
    inventoryItemType: "Machine",
    status: "Onboarding",
    usage: {
      standard: { value: null, unitOfMeasure: { name: "Hours" } },
      metric: { value: 0, unitOfMeasure: { name: "Hours" } },
    },
    description: "",
    approvalStatus: "Pending",
    buyItNowEnabled: false,
    buyItNowOnly: false,
    rpoEnabled: false,
    rentalRate: null,
    rentalRateDaily: null,
    rentalRateWeekly: null,
    purchasePrice: null,
  } as InventoryItem;

  // Dropdown data
  machineTypes: DropDownItem[] = [];
  makes: DropDownItem[] = [];
  models: DropDownItem[] = [];
  yearDropdownItems: DropDownItem[] = [];
  machineTypeUsageMeasurementById: Map<string, string> = new Map();
  currentUsageMeasurementName: string = "Hours";

  /** Catalog rows from Azure Search for the selected typeDefinitionId (make/model lists are derived from this). */
  private catalogRowsForType: { id: string; make: string; model: string; displayName?: string }[] = [];

  /** Avoids re-querying Azure Search when `refreshCatalogForMachineType` is called repeatedly for the same type (e.g. confirmation step navigation). */
  private catalogFetchTypeDefinitionId: string | null = null;

  // UI state
  loading: boolean = false;
  showSuggestedRateText: boolean = false;
  showLocationTypeahead: boolean = false;
  yardDropdownItems: any[] = [];
  locationCheckboxCards: CheckboxCard[] = [];
  locationYardMap: Map<string, any> = new Map(); // Map checkbox card value to yard object
  selectedLocation: Location;
  paymentMethodId: string;

  // Image analysis state
  analyzingImage: boolean = false;
  analysisResult: MachineAnalysisResult | null = null;
  showAnalysisResult: boolean = false;

  // UI flow state
  showMainForm: boolean = false;

  // Confirmation step state
  showConfirmationSteps: boolean = false;
  showFinalConfirmation: boolean = false;
  currentConfirmationStep: number = 0;
  confirmationSteps: string[] = [];
  confirmedData: any = {
    type: null,
    make: null,
    model: null,
    year: null,
    hours: null,
    serialNumber: null,
    description: null,
    listingOptions: [],
    rentalRates: {
      monthly: null,
      weekly: null,
      daily: null,
    },
    location: null,
    paymentMethod: null,
    termsAccepted: false,
    potentialSaleValue: null,
    marketAnalysis: null,
  };
  showDropdown: boolean = false;
  selectedCorrectionValue: string = "";
  currentDropdownOptions: DropDownItem[] = [];
  selectedListingOptions: string[] = [];
  showManualInput: boolean = false;

  // Account and user data
  account: Account;
  user: any;

  // Terms and validation
  terms = [
    {
      label:
        "I agree to keep the availability status of my equipment current at all times. I understand that failure to do so will negatively impact my owner status and may result in account suspension.",
      checked: false,
    },
    {
      label: 'I agree to the&nbsp;<a href="https://support.rubbl.com/cancellation-policy-owners" target="_blank">cancellation policy</a>.',
      checked: false,
    },
  ];

  validationErrors: string[] = [];
  checkboxesForRentRtoSellItNow: CheckboxCard[] = [];

  constructor(
    private accountService: AccountService,
    private authService: AuthenticationService,
    private locationPipe: LocationPipe,
    private inventoryService: InventoryService,
    private catalogService: CatalogService,
    private toastrService: ToastrService,
    private router: Router,
    private logService: LogService,
    private stripeService: StripeService,
    private currencyPipe: CurrencyPipe,
    private azureSearchService: AzureSearchService,
    private openAIService: OpenAIService,
    private inventoryTypeService: InventoryTypeService,
  ) {}

  ngOnInit(): void {
    this.stripeService.init();
    this.setupUserAndAccount();
    this.setupYearDropdown();
    this.loadMachineTypes();
    this.initializeDefaultListingOptions();
  }

  private initializeDefaultListingOptions(): void {
    // Initialize with default listing options (will be enhanced with earnings after location)
    this.checkboxesForRentRtoSellItNow = [
      {
        value: "rent",
        label: "Rent",
        checked: true,
        body: "Rentals are offered on a weekly or monthly basis only, no daily rentals.",
      },
      {
        value: "rpo",
        label: "Rent To Own",
        checked: false,
        disabled: false, // Will be dynamically updated based on rent selection
        body: "Provide renters with a flexible purchase option at the end of the rental, allowing you to sell your machine at a retail price.",
      },
      {
        value: "buyItNow",
        label: "Sell It Now",
        checked: false,
        body: "List your machine for sale directly on the Rubbl marketplace.",
      },
    ];
  }

  private setupUserAndAccount(): void {
    this.user = this.authService.getUserNonAsync();
    this.entity.accountId = this.user.profile.account_id;

    this.accountService.get(this.entity.accountId).subscribe(account => {
      if (account.affiliateId) {
        this.entity.affiliateId = account.affiliateId;
      }
      this.account = account;
      this.setupYardDropdown();
    });
  }

  private setupYearDropdown(): void {
    let currentYear = new Date().getFullYear() + 2;
    let earliestYear = 2000;
    while (currentYear >= earliestYear) {
      this.yearDropdownItems.push({
        label: currentYear.toString(),
        value: currentYear.toString(),
      } as DropDownItem);
      currentYear -= 1;
    }
  }

  private setupYardDropdown(): void {
    this.accountService.get(this.entity.accountId).subscribe(account => {
      this.yardDropdownItems = account.yards.map(d => ({
        label: this.locationPipe.transform(d, "full", false) || "",
        value: d,
      }));

      // Setup location checkbox cards
      if (account.yards && account.yards.length > 0) {
        this.locationYardMap.clear(); // Clear the map first

        this.locationCheckboxCards = account.yards.map(yard => {
          const yardLabel = this.locationPipe.transform(yard, "full", false) || "";
          // Store the yard object in the map
          this.locationYardMap.set(yardLabel, yard);

          return {
            value: yardLabel,
            label: yardLabel,
            checked: false,
          };
        });

        // Add "Add new address" option at the end
        this.locationCheckboxCards.push({
          value: "new",
          label: "Add new address",
          checked: false,
          body: "Enter a new location for your machine",
        });
      } else {
        // No existing locations, show typeahead immediately
        this.locationCheckboxCards = [];
        this.showLocationTypeahead = true;
      }
    });
  }

  private loadMachineTypes(): void {
    const searchRequest = new AzureSearchRequest();
    searchRequest.filter = "status eq 'Published' and definitionType eq 'MachineType'";
    // Only fields that exist on the type-definitions index (see TypeDefinitionModel); usage lives on the API.
    searchRequest.select = "primaryType,name,id";
    searchRequest.top = 1000;
    searchRequest.orderby = "name asc";
    searchRequest.search = ""; // No search term, just filter

    this.azureSearchService.search<any>(searchRequest, "type-definitions").subscribe({
      next: result => {
        if (result && result.value) {
          this.machineTypeUsageMeasurementById.clear();
          this.machineTypes = result.value.map(item => ({
            label: item.name,
            value: item.id,
          }));
        }
      },
      error: error => {
        console.error("Failed to load machine types:", error);
        this.toastrService.error("Failed to load machine types. Please refresh the page.");
      },
    });
  }

  // Machine type/make/model selection methods
  onMachineTypeChange(type: string): void {
    this.selectedMachineType = type;
    this.selectedMachineMake = "";
    this.selectedMachineModel = "";
    this.entity.rentalRate = null;
    this.entity.rentalRateWeekly = null;
    this.entity.rentalRateDaily = null;
    this.showSuggestedRateText = false;

    if (!type) {
      this.applyUsageMeasurementByTypeDefinitionId(null);
      this.clearCatalogPicklists();
      return;
    }
    this.applyUsageMeasurementByTypeDefinitionId(type);
    this.refreshCatalogForMachineType(type).subscribe();
  }

  onMachineMakeChange(make: string): void {
    this.selectedMachineMake = make;
    this.selectedMachineModel = "";
    this.entity.rentalRate = null;
    this.entity.rentalRateWeekly = null;
    this.entity.rentalRateDaily = null;
    this.showSuggestedRateText = false;

    this.rebuildModelsDropdownForMake(make);
  }

  onMachineModelChange(model: string): void {
    this.selectedMachineModel = model;
  }

  private clearCatalogPicklists(): void {
    this.catalogRowsForType = [];
    this.makes = [];
    this.models = [];
    this.catalogFetchTypeDefinitionId = null;
  }

  /** Azure Search `catalog`: published rows for `typeDefinitionId` → cached rows + make dropdown; models cleared until a make is chosen. */
  private refreshCatalogForMachineType(typeDefinitionId: string): Observable<void> {
    if (!typeDefinitionId) {
      this.clearCatalogPicklists();
      return of(void 0);
    }

    if (this.catalogFetchTypeDefinitionId === typeDefinitionId) {
      return of(void 0);
    }

    const req = new AzureSearchRequest();
    req.top = 10000;
    req.select = "id,make,model,displayName";
    req.filter = `typeDefinitionId eq '${typeDefinitionId}' and status eq 'Published'`;
    req.orderby = "make, model";

    return this.azureSearchService.search<any>(req, "catalog").pipe(
      tap(res => {
        this.catalogRowsForType = (res.value || []).map((row: any) => ({
          id: row.id,
          make: (row.make || "").trim(),
          model: (row.model || "").trim(),
          displayName: row.displayName,
        }));
        this.catalogFetchTypeDefinitionId = typeDefinitionId;
        this.rebuildMakesDropdown();
        this.models = [];
      }),
      map(() => void 0),
      catchError(err => {
        console.error("Failed to load catalog for machine type:", err);
        this.toastrService.error("Failed to load makes for this machine type.");
        this.clearCatalogPicklists();
        return of(void 0);
      }),
    );
  }

  private rebuildMakesDropdown(): void {
    const seen = new Set<string>();
    const makesList: DropDownItem[] = [];
    for (const row of this.catalogRowsForType) {
      const make = row.make;
      if (!make) {
        continue;
      }
      const key = make.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      makesList.push({ label: make, value: make });
    }
    makesList.sort((a, b) => a.label.localeCompare(b.label));
    this.makes = makesList;
  }

  private findCatalogIdForSelectedMakeModel(): string | null {
    if (!this.selectedMachineMake?.trim() || !this.selectedMachineModel?.trim() || !this.catalogRowsForType.length) {
      return null;
    }
    const makeLc = this.selectedMachineMake.trim().toLowerCase();
    const modelLc = this.selectedMachineModel.trim().toLowerCase();
    const row = this.catalogRowsForType.find(
      r => (r.make || "").trim().toLowerCase() === makeLc && (r.model || "").trim().toLowerCase() === modelLc,
    );
    return row?.id ?? null;
  }

  private rebuildModelsDropdownForMake(make: string): void {
    if (!make) {
      this.models = [];
      return;
    }
    const makeLc = make.toLowerCase();
    const seen = new Set<string>();
    const modelsList: DropDownItem[] = [];
    for (const row of this.catalogRowsForType) {
      if (!row.make || row.make.toLowerCase() !== makeLc) {
        continue;
      }
      const model = row.model;
      if (!model) {
        continue;
      }
      const key = model.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const label = row.displayName && row.displayName !== model ? `${model} (${row.displayName})` : model;
      modelsList.push({ label, value: model });
    }
    modelsList.sort((a, b) => a.label.localeCompare(b.label));
    this.models = modelsList;
  }

  /** Map AI/confirmed text to a picklist `value` (catalog make/model string). */
  private matchPicklistOption(items: DropDownItem[], raw: string, kind: "make" | "model"): string | null {
    if (!raw?.trim() || !items.length) {
      return null;
    }
    const t = raw.trim().toLowerCase();
    const exact = items.find(m => m.label.toLowerCase() === t || String(m.value).toLowerCase() === t);
    if (exact) {
      return String(exact.value);
    }
    if (kind === "make") {
      return items.find(m => {
        const L = m.label.toLowerCase();
        return L.includes(t) || t.includes(L);
      })?.value ?? null;
    }
    return items.find(m => {
      const L = m.label.toLowerCase();
      return L === t || L.startsWith(t + " (") || L.includes(t);
    })?.value ?? null;
  }

  /** After catalog rows load, align `selectedMachineMake` / `selectedMachineModel` with `confirmedData`. */
  private syncFormPicklistsFromConfirmedData(): void {
    if (this.confirmedData.make) {
      this.selectedMachineMake = this.matchPicklistOption(this.makes, this.confirmedData.make, "make") ?? this.confirmedData.make;
      this.rebuildModelsDropdownForMake(this.selectedMachineMake);
    } else {
      this.selectedMachineMake = "";
    }
    if (this.confirmedData.model) {
      this.selectedMachineModel = this.matchPicklistOption(this.models, this.confirmedData.model, "model") ?? this.confirmedData.model;
    } else {
      this.selectedMachineModel = "";
    }
  }

  getTypeDefinitionIdForConfirmationFlow(): string | null {
    if (this.selectedMachineType) {
      return this.selectedMachineType;
    }
    if (!this.confirmedData?.type || !this.machineTypes?.length) {
      return null;
    }
    const nt = String(this.confirmedData.type).toLowerCase().trim();
    if (!nt) {
      return null;
    }
    const exact = this.machineTypes.find(t => t.label.toLowerCase() === nt);
    if (exact) {
      return String(exact.value);
    }
    if (nt.length < 2) {
      return null;
    }
    const partial = this.machineTypes.filter(t => t.label.toLowerCase().includes(nt));
    if (!partial.length) {
      return null;
    }
    partial.sort((a, b) => a.label.length - b.label.length);
    return String(partial[0].value);
  }

  private preloadCatalogForConfirmationStep(done: () => void): boolean {
    const step = this.getCurrentStepType();
    const typeId = this.getTypeDefinitionIdForConfirmationFlow();
    const forMake = step === "make" || step === "make-manual";
    const forModel = step === "model" || step === "model-manual";
    if (!typeId || (!forMake && !forModel)) {
      return false;
    }

    this.selectedMachineType = typeId;
    this.refreshCatalogForMachineType(typeId).subscribe(() => {
      if (forModel && this.confirmedData.make) {
        const make = this.matchPicklistOption(this.makes, this.confirmedData.make, "make") ?? this.confirmedData.make;
        this.rebuildModelsDropdownForMake(make);
      }
      done();
    });
    return true;
  }

  // Rental and pricing methods
  get isRentSelected(): boolean {
    return this.entity.rentalRate !== -1;
  }

  handleCheckboxChange(checkboxes: CheckboxCard[]): void {
    checkboxes.forEach(checkbox => {
      switch (checkbox.value) {
        case "rent":
          if (!checkbox.checked) {
            this.entity.rentalRate = -1;
            this.entity.rentalRateWeekly = null;
            this.entity.rentalRateDaily = null;
            this.entity.rpoEnabled = false;
          } else {
            this.entity.rentalRate = this.entity.rentalRate !== -1 && this.entity.rentalRate !== null ? this.entity.rentalRate : null;
          }
          break;
        case "buyItNow":
          this.entity.buyItNowEnabled = checkbox.checked;
          break;
        case "rpo":
          if (this.entity.rentalRate !== -1) {
            this.entity.rpoEnabled = checkbox.checked;
          } else {
            this.entity.rpoEnabled = false;
          }
          break;
      }
    });
  }

  onRentalRateChange(value: string | number): void {
    let numericValue = 0;
    if (typeof value === "string") {
      numericValue = parseFloat(value.replace(/,/g, "")) || 0;
    } else {
      numericValue = value;
    }

    const rates = this.inventoryService.calculateRentalRates(numericValue, this.account?.companyType);
    this.entity.rentalRateWeekly = rates.weekly;
    this.entity.rentalRateDaily = rates.daily;
  }

  getDailyEarnings(): number {
    if (!this.entity.rentalRateDaily) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer"
      ? this.entity.rentalRateDaily * 0.8
      : this.entity.rentalRateDaily * 0.7;
  }

  getWeeklyEarnings(): number {
    if (!this.entity.rentalRateWeekly) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer"
      ? this.entity.rentalRateWeekly * 0.8
      : this.entity.rentalRateWeekly * 0.7;
  }

  getMonthlyEarnings(): number {
    if (!this.entity.rentalRate) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer"
      ? this.entity.rentalRate * 0.8
      : this.entity.rentalRate * 0.7;
  }

  // Methods for AI analysis results
  capitalizeType(machineType: string): string {
    return machineType
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  calculateDailyEarnings(dailyRate: number): number {
    if (!dailyRate) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? dailyRate * 0.8 : dailyRate * 0.7;
  }

  calculateWeeklyEarnings(weeklyRate: number): number {
    if (!weeklyRate) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? weeklyRate * 0.8 : weeklyRate * 0.7;
  }

  calculateMonthlyEarnings(monthlyRate: number): number {
    if (!monthlyRate) return 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? monthlyRate * 0.8 : monthlyRate * 0.7;
  }

  // Confirmation step methods
  getCurrentStepType(): string {
    return this.confirmationSteps[this.currentConfirmationStep];
  }

  getCurrentStepValue(): string {
    const stepType = this.getCurrentStepType();
    switch (stepType) {
      case "type":
        return this.capitalizeType(this.confirmedData.type);
      case "make":
        return this.confirmedData.make;
      case "model":
        return this.confirmedData.model;
      case "year":
        return this.confirmedData.year;
      default:
        return "";
    }
  }

  getCurrentStepQuestion(): string {
    const stepType = this.getCurrentStepType();
    const value = this.getCurrentStepValue();

    switch (stepType) {
      case "type":
        return `Is your machine a ${value}?`;
      case "make":
        return `Is your machine made by ${value}?`;
      case "model":
        return `Is your machine model ${value}?`;
      case "year":
        return `Is your machine from ${value}?`;
      case "type-manual":
        return "What type of machine is this?";
      case "make-manual":
        return "What make is your machine?";
      case "model-manual":
        return "What model is your machine?";
      case "year-manual":
        return "What year is your machine?";
      case "hours":
        return this.isMileageUsage()
          ? "How many miles does your machine have?"
          : "How many hours does your machine have?";
      case "serialNumber":
        return "What is your machine's serial number?";
      case "listingOptions":
        return "How would you like to list your machine?";
      case "rentalRates":
        return "Confirm your rental rates?";
      case "location":
        return "Where is your machine located?";
      case "paymentMethod":
        return "Please select a payment method";
      case "terms":
        return "Please accept the terms and conditions";
      default:
        return "";
    }
  }

  canConfirmCurrentStep(): boolean {
    const stepType = this.getCurrentStepType();

    switch (stepType) {
      case "location":
        return !!(this.entity.location && this.entity.location.address);

      case "rentalRates":
        return !!this.confirmedData.rentalRates.daily;

      // Add other step validations as needed
      default:
        return true;
    }
  }

  confirmCurrentStep(): void {
    const stepType = this.getCurrentStepType();

    // Apply step-specific validation before confirming
    switch (stepType) {
      case "location":
        if (!this.entity.location || !this.entity.location.address) {
          this.toastrService.error("Please select or enter a valid location before proceeding.");
          return;
        }
        this.confirmedData.location = this.entity.location;
        break;

      case "rentalRates":
        if (!this.confirmedData.rentalRates.daily) {
          this.toastrService.error("Please enter rental rates before proceeding.");
          return;
        }
        break;

      // Add other step validations as needed
      default:
        break;
    }

    this.nextConfirmationStep();
  }

  rejectCurrentStep(): void {
    const stepType = this.getCurrentStepType();

    if (stepType === "rentalRates") {
      // Pre-populate with AI suggested rates when user wants to customize (20% discount applied)
      if (this.hasSuggestedRates()) {
        this.confirmedData.rentalRates.monthly = Math.round(this.analysisResult.monthlyRate * 0.8).toString();
        this.confirmedData.rentalRates.weekly = this.analysisResult.weeklyRate ? Math.round(this.analysisResult.weeklyRate * 0.8).toString() : "";
        this.confirmedData.rentalRates.daily = this.analysisResult.dailyRate ? Math.round(this.analysisResult.dailyRate * 0.8).toString() : "";
      }
    }

    this.showDropdown = true;
    this.showManualInput = false;
    this.selectedCorrectionValue = ""; // Reset dropdown selection

    const finishRejectOptions = () => {
      this.currentDropdownOptions = this.getDropdownOptions();
    };

    if (stepType === "make" || stepType === "make-manual" || stepType === "model" || stepType === "model-manual") {
      if (this.preloadCatalogForConfirmationStep(finishRejectOptions)) {
        return;
      }
    }

    finishRejectOptions();
  }

  saveCorrection(value: string): void {
    const stepType = this.getCurrentStepType();

    if (!value && stepType !== "serialNumber") return;

    this.saveValueToConfirmedData(value);
    this.showDropdown = false;
    this.nextConfirmationStep();
  }

  getDropdownOptions(): DropDownItem[] {
    const stepType = this.getCurrentStepType();
    let options: DropDownItem[] = [];

    switch (stepType) {
      case "type":
      case "type-manual":
        options = this.machineTypes;
        break;
      case "make":
      case "make-manual":
        options = this.makes;
        break;
      case "model":
      case "model-manual":
        options = this.models;
        break;
      case "year":
      case "year-manual":
        options = this.yearDropdownItems;
        break;
      default:
        return [];
    }

    return options;
  }

  shouldShowDropdown(): boolean {
    const stepType = this.getCurrentStepType();
    return this.showDropdown && stepType !== "rentalRates";
  }

  nextConfirmationStep(): void {
    this.currentConfirmationStep++;
    this.showDropdown = false;
    this.showManualInput = false;
    window.scrollTo(0, 0);

    if (this.currentConfirmationStep >= this.confirmationSteps.length) {
      this.finishConfirmation();
      return;
    }

    // Initialize selected listing options when we reach the listing options step
    if (this.getCurrentStepType() === "listingOptions") {
      this.selectedListingOptions = this.checkboxesForRentRtoSellItNow.filter(card => card.checked).map(card => card.value);
    }

    const finishStep = () => {
      this.currentDropdownOptions = this.getDropdownOptions();
      this.restoreStepValue();
    };

    if (this.preloadCatalogForConfirmationStep(finishStep)) {
      return;
    }

    finishStep();
  }

  onDropdownChange(value: string): void {
    this.selectedCorrectionValue = value;
    this.showManualInput = false;
    if (value) {
      this.saveValueToConfirmedData(value);
    }
  }

  showManualInputForDropdown(): void {
    const step = this.getCurrentStepType();
    if (step === "type" || step === "type-manual") {
      return;
    }
    this.showManualInput = true;
    this.selectedCorrectionValue = "";
  }

  /** Machine type must always be chosen from the published machine-type list, never free-typed. */
  allowManualEntryForMachineTypeStep(): boolean {
    const step = this.getCurrentStepType();
    return step !== "type" && step !== "type-manual";
  }

  hideManualInputForDropdown(): void {
    this.showManualInput = false;
    this.selectedCorrectionValue = "";
  }

  saveValueToConfirmedData(value: string): void {
    const stepType = this.getCurrentStepType();

    let selectedOption: DropDownItem | undefined;
    let displayValue = value;
    if (this.currentDropdownOptions.length > 0) {
      selectedOption = this.currentDropdownOptions.find(option => String(option.value) === String(value));
      if (selectedOption) {
        displayValue = selectedOption.label;
      }
    }

    switch (stepType) {
      case "type":
      case "type-manual":
        this.confirmedData.type = displayValue;
        if (selectedOption) {
          this.selectedMachineType = String(selectedOption.value);
        } else {
          this.selectedMachineType = "";
        }
        this.applyUsageMeasurementByTypeDefinitionId(this.selectedMachineType || null);
        break;
      case "make":
      case "make-manual":
        this.confirmedData.make = selectedOption ? String(selectedOption.value) : value;
        break;
      case "model":
      case "model-manual":
        // Store canonical catalog model name (value); label may include displayName for UI only.
        this.confirmedData.model = selectedOption ? String(selectedOption.value) : value;
        break;
      case "year":
      case "year-manual":
        this.confirmedData.year = displayValue;
        break;
      case "hours":
        this.confirmedData.hours = displayValue;
        break;
      case "serialNumber":
        this.confirmedData.serialNumber = displayValue ? displayValue.toUpperCase() : "";
        break;
    }
  }

  finishConfirmation(): void {
    this.showConfirmationSteps = false;
    this.showFinalConfirmation = true;
    window.scrollTo(0, 0);
  }

  goBackFromFinalConfirmation(): void {
    this.showFinalConfirmation = false;
    this.showConfirmationSteps = true;
    this.currentConfirmationStep = this.confirmationSteps.length - 1;
    this.restoreStepValue();
    window.scrollTo(0, 0);
  }

  goBackConfirmationStep(): void {
    if (
      this.getCurrentStepType() === "location" &&
      this.showLocationTypeahead &&
      this.locationCheckboxCards.length > 0
    ) {
      this.entity.location = null;
      this.restoreStepValue();
      return;
    }

    if (this.currentConfirmationStep > 0) {
      this.currentConfirmationStep--;
      this.showDropdown = false;
      this.showManualInput = false;

      const finishStep = () => {
        this.currentDropdownOptions = this.getDropdownOptions();
        this.restoreStepValue();
      };

      if (this.preloadCatalogForConfirmationStep(finishStep)) {
        return;
      }

      finishStep();
    }
  }

  private restoreStepValue(): void {
    const stepType = this.getCurrentStepType();
    let savedValue: string | null = null;

    switch (stepType) {
      case "type":
      case "type-manual":
        savedValue = this.confirmedData.type;
        break;
      case "make":
      case "make-manual":
        savedValue = this.confirmedData.make;
        break;
      case "model":
      case "model-manual":
        savedValue = this.confirmedData.model;
        break;
      case "year":
      case "year-manual":
        savedValue = this.confirmedData.year;
        break;
      case "hours":
        savedValue = this.confirmedData.hours;
        break;
      case "serialNumber":
        savedValue = this.confirmedData.serialNumber;
        break;
      case "location":
        if (this.locationCheckboxCards.length > 0) {
          this.showLocationTypeahead = false;
          this.locationCheckboxCards.forEach(card => card.checked = false);
          if (this.entity.location) {
            for (const [label, yard] of this.locationYardMap) {
              if (yard === this.entity.location) {
                const matchingCard = this.locationCheckboxCards.find(c => c.value === label);
                if (matchingCard) matchingCard.checked = true;
                break;
              }
            }
          }
        }
        return;
      default:
        this.selectedCorrectionValue = "";
        return;
    }

    if (!savedValue) {
      this.selectedCorrectionValue = "";
      return;
    }

    if (stepType.includes("-manual") || stepType === "year" || stepType === "type" || stepType === "make" || stepType === "model") {
      const options = this.getDropdownOptions();
      this.currentDropdownOptions = options;
      const matchingOption = options.find(
        option =>
          String(option.value) === String(savedValue) ||
          option.label === savedValue ||
          option.label.toLowerCase().startsWith(String(savedValue).toLowerCase() + " ("),
      );
      if (matchingOption) {
        this.selectedCorrectionValue = String(matchingOption.value);
      } else if (stepType === "type" || stepType === "type-manual") {
        this.showManualInput = false;
        this.selectedCorrectionValue = "";
      } else {
        this.showManualInput = true;
        this.selectedCorrectionValue = savedValue;
      }
    } else {
      this.selectedCorrectionValue = savedValue;
    }
  }

  createMachineFromConfirmedData(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.applyConfirmedDataToEntity().subscribe({
      next: () => {
        console.log("Creating machine with entity:", {
          rpoEnabled: this.entity.rpoEnabled,
          buyItNowEnabled: this.entity.buyItNowEnabled,
          rentalRate: this.entity.rentalRate,
          listingOptions: this.confirmedData.listingOptions,
        });

        this.createMachine();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  applyConfirmedDataToEntity(): Observable<void> {
    if (this.confirmedData.year) {
      this.entity.year = this.confirmedData.year;
    }

    if (this.confirmedData.hours) {
      this.entity.usage.standard.value = parseInt(this.confirmedData.hours, 10);
    }

    if (this.confirmedData.serialNumber) {
      this.entity.serialNumber = this.confirmedData.serialNumber;
    }

    if (this.confirmedData.listingOptions.includes("rent")) {
      this.entity.rentalRate = this.entity.rentalRate !== -1 ? this.entity.rentalRate : null;
    } else {
      this.entity.rentalRate = -1;
    }
    this.entity.rpoEnabled = this.confirmedData.listingOptions.includes("rpo") && this.confirmedData.listingOptions.includes("rent");
    this.entity.buyItNowEnabled = this.confirmedData.listingOptions.includes("buyItNow");

    if (this.confirmedData.rentalRates.monthly) {
      this.entity.rentalRate = parseInt(this.confirmedData.rentalRates.monthly.replace(/,/g, ""), 10);
    }
    if (this.confirmedData.rentalRates.weekly) {
      this.entity.rentalRateWeekly = parseInt(this.confirmedData.rentalRates.weekly.replace(/,/g, ""), 10);
    }
    if (this.confirmedData.rentalRates.daily) {
      this.entity.rentalRateDaily = parseInt(this.confirmedData.rentalRates.daily.replace(/,/g, ""), 10);
    }

    if (!this.confirmedData.rentalRates.monthly && this.analysisResult?.monthlyRate && this.analysisResult.monthlyRate > 0) {
      const discountedMonthlyRate = Math.round(this.analysisResult.monthlyRate * 0.8);
      this.entity.rentalRate = discountedMonthlyRate;
      this.onRentalRateChange(discountedMonthlyRate);
    }
    if (!this.confirmedData.rentalRates.weekly && this.analysisResult?.weeklyRate && this.analysisResult.weeklyRate > 0) {
      this.entity.rentalRateWeekly = Math.round(this.analysisResult.weeklyRate * 0.8);
    }
    if (!this.confirmedData.rentalRates.daily && this.analysisResult?.dailyRate && this.analysisResult.dailyRate > 0) {
      this.entity.rentalRateDaily = Math.round(this.analysisResult.dailyRate * 0.8);
    }

    this.showSuggestedRateText = true;

    if (this.confirmedData.type) {
      const normalizedType = this.confirmedData.type.toLowerCase().trim();
      if (normalizedType) {
        let matchingType = this.machineTypes.find(t => t.label.toLowerCase() === normalizedType);
        if (!matchingType && normalizedType.length >= 2) {
          const partial = this.machineTypes.filter(t => t.label.toLowerCase().includes(normalizedType));
          if (partial.length) {
            partial.sort((a, b) => a.label.length - b.label.length);
            matchingType = partial[0];
          }
        }

        if (matchingType) {
          this.selectedMachineType = matchingType.value;
          this.applyUsageMeasurementByTypeDefinitionId(this.selectedMachineType);
          return this.refreshCatalogForMachineType(matchingType.value).pipe(
            tap(() => this.syncFormPicklistsFromConfirmedData()),
            map(() => void 0),
          );
        }
      }
    }

    this.selectedMachineMake = this.confirmedData.make || "";
    this.selectedMachineModel = this.confirmedData.model || "";
    this.applyUsageMeasurementByTypeDefinitionId(this.selectedMachineType || null);
    this.clearCatalogPicklists();
    return of(void 0);
  }

  // Helper methods for question-based flow
  isSpecialQuestionType(): boolean {
    const stepType = this.getCurrentStepType();
    return ["hours", "serialNumber", "listingOptions", "rentalRates", "location", "paymentMethod", "terms"].includes(stepType);
  }

  getManualEntryLinkText(): string {
    const stepType = this.getCurrentStepType();
    if (stepType === "make" || stepType === "make-manual") {
      return "Make not found, add it here to continue.";
    }
    if (stepType === "model" || stepType === "model-manual") {
      return "Model not found, add it here to continue.";
    }
    if (stepType === "year" || stepType === "year-manual") {
      return "Year not found, add it here to continue.";
    }
    return "Enter your own value.";
  }

  getInputLabel(): string {
    const stepType = this.getCurrentStepType();
    switch (stepType) {
      case "hours":
        return `Enter machine ${this.getUsageInputLabel().toLowerCase()}:`;
      case "serialNumber":
        return "Enter serial number:";
      case "type-manual":
        return "Select machine type:";
      case "make-manual":
        return "Select machine make:";
      case "model-manual":
        return "Select machine model:";
      case "year-manual":
        return ""; // No label needed, question is already displayed
      default:
        return `Enter ${stepType.replace("-manual", "")}:`;
    }
  }

  getInputPlaceholder(): string {
    const stepType = this.getCurrentStepType();
    switch (stepType) {
      case "hours":
        return this.isMileageUsage() ? "e.g. 50000" : "e.g. 750";
      case "serialNumber":
        return "Enter serial number";
      default:
        return `Enter ${stepType.replace("-manual", "")}`;
    }
  }

  updateListingOptions(checkboxes: CheckboxCard[]): void {
    this.selectedListingOptions = [];
    checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        this.selectedListingOptions.push(checkbox.value);
      }
    });
  }

  updateSingleListingOption(optionValue: string, checkboxCards: CheckboxCard[]): void {
    const card = checkboxCards[0]; // Since we're passing a single card array

    if (card.checked) {
      // Add the option if not already present
      if (!this.selectedListingOptions.includes(optionValue)) {
        this.selectedListingOptions.push(optionValue);
      }
    } else {
      // Remove the option
      this.selectedListingOptions = this.selectedListingOptions.filter(option => option !== optionValue);
    }

    // Auto-disable RPO if rent is not selected
    if (optionValue === "rent" && !card.checked) {
      this.selectedListingOptions = this.selectedListingOptions.filter(option => option !== "rpo");
    }
  }

  onListingOptionsChange(checkboxCards: CheckboxCard[]): void {
    // Handle RPO dependency logic - RPO should be disabled if rent is not selected
    const rentCard = checkboxCards.find(card => card.value === "rent");
    const rpoCard = checkboxCards.find(card => card.value === "rpo");

    if (rpoCard) {
      if (rentCard && !rentCard.checked) {
        // If rent is not selected, disable and uncheck RPO
        rpoCard.disabled = true;
        rpoCard.checked = false;
      } else {
        // If rent is selected, enable RPO
        rpoCard.disabled = false;
      }
    }

    // Update the checkbox cards array
    this.checkboxesForRentRtoSellItNow = checkboxCards;

    // Update selected listing options from checkbox cards
    this.selectedListingOptions = checkboxCards.filter(card => card.checked).map(card => card.value);
  }

  saveListingOptions(): void {
    this.confirmedData.listingOptions = [...this.selectedListingOptions];
    console.log("Saved listing options:", this.confirmedData.listingOptions);
    this.nextConfirmationStep();
  }

  canProceedWithListingOptions(): boolean {
    // Get currently selected options from checkbox cards directly
    const currentlySelected = this.checkboxesForRentRtoSellItNow.filter(card => card.checked).map(card => card.value);

    // Must have at least one option selected
    if (currentlySelected.length === 0) {
      return false;
    }

    return true;
  }

  saveTerms(): void {
    this.confirmedData.termsAccepted = this.terms.every(t => t.checked);
    this.nextConfirmationStep();
  }

  saveLocation(): void {
    // Validate that a location is actually selected
    if (!this.entity.location || !this.entity.location.address) {
      this.toastrService.error("Please select or enter a valid location before proceeding.");
      return;
    }

    // If it's a new location (using typeahead), create the location first
    if (this.showLocationTypeahead && this.entity.location.address) {
      const existingYard = this.yardDropdownItems.find(yard => yard.value.address && yard.value.address.address1 === this.entity.location.address.address1);

      if (!existingYard) {
        // Set a name for the new location
        this.entity.location.name = `${this.entity.location.address.city}, ${this.entity.location.address.stateProvince}`;

        // Create the location
        this.loading = true;
        this.accountService.addLocation(this.entity.accountId, this.entity.location as any).subscribe({
          next: result => {
            this.loading = false;

            // Update the yards dropdown with the new location
            this.setupYardDropdown();

            this.confirmedData.location = this.entity.location;

            // Now that we have location, get enhanced rental rates with zip code
            this.getEnhancedRentalRatesWithLocation();

            this.nextConfirmationStep();
          },
          error: error => {
            this.loading = false;
            console.error("Error creating location:", error);

            if (error.status === 409) {
              this.toastrService.error("The selected location already exists. Please choose a different location.");
            } else {
              this.toastrService.error("Failed to create location. Please try again.");
            }
          },
        });
        return;
      }
    }

    this.confirmedData.location = this.entity.location;

    // Now that we have location, get enhanced rental rates with zip code
    this.getEnhancedRentalRatesWithLocation();

    this.nextConfirmationStep();
  }

  private getEnhancedRentalRatesWithLocation(): void {
    if (!this.confirmedData.make || !this.confirmedData.model || !this.entity.location?.address?.postalCode) {
      return; // Skip if we don't have the required data
    }

    // Call OpenAI with location, year, and usage (hours or odometer miles) for rental rate suggestions
    const zipCode = this.entity.location.address.postalCode;
    const city = this.entity.location.address.city;
    const state = this.entity.location.address.stateProvince;
    const year = this.confirmedData.year || "2020";
    const usageValue = this.confirmedData.hours || "0";
    const isMileage = this.isMileageUsage();

    this.getLocationBasedRentalRates(
      this.confirmedData.make,
      this.confirmedData.model,
      year,
      usageValue,
      `${city}, ${state} ${zipCode}`,
      isMileage,
    );
  }

  private getLocationBasedRentalRates(
    make: string,
    model: string,
    year: string,
    usageValue: string,
    location: string,
    isMileageBased: boolean,
  ): void {
    const usageWord = isMileageBased ? "miles" : "hours";
    console.log(`Getting enhanced rental rates for ${year} ${make} ${model} with ${usageValue} ${usageWord} in ${location}`);

    this.openAIService.getLocationBasedRentalRates(make, model, year, usageValue, location, isMileageBased).subscribe({
      next: (result: LocationBasedRentalRatesResult | null) => {
        if (result) {
          // Update rental rates with location-based suggestions (20% discount applied)
          this.confirmedData.rentalRates.monthly = Math.round(result.monthlyRate * 0.8).toString();
          this.confirmedData.rentalRates.weekly = Math.round(result.weeklyRate * 0.8).toString();
          this.confirmedData.rentalRates.daily = Math.round(result.dailyRate * 0.8).toString();

          // Store potential sale value for earnings calculations
          this.confirmedData.potentialSaleValue = result.potentialSaleValue;
          this.confirmedData.marketAnalysis = result.marketAnalysis;

          // Update the listing options with earnings information
          this.updateListingOptionsWithEarnings(result);
        } else {
          // No result returned, show error messages
          this.updateListingOptionsWithErrors();
        }
      },
      error: error => {
        console.error("Error getting location-based rental rates:", error);
        // Show error messages and allow user to continue
        this.updateListingOptionsWithErrors();
      },
    });
  }

  private updateListingOptionsWithEarnings(rateResult: LocationBasedRentalRatesResult): void {
    // Apply 20% discount to OpenAI rates
    const monthlyRate = Math.round(rateResult.monthlyRate * 0.8);
    const weeklyRate = Math.round(rateResult.weeklyRate * 0.8);
    const dailyRate = Math.round(rateResult.dailyRate * 0.8);
    const potentialSaleValue = rateResult.potentialSaleValue;

    // Calculate earnings (assuming Rubbl takes ~15% commission)
    const monthlyEarnings = Math.round(monthlyRate * 0.7);
    const weeklyEarnings = Math.round(weeklyRate * 0.7);
    const dailyEarnings = Math.round(dailyRate * 0.7);

    // Calculate potential sale proceeds based on account type
    // Dealers/RentalCompany: 6.5% fee (93.5% proceeds), Everyone else: 10% fee (90% proceeds)
    const isDealerOrRentalCompany = this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer";
    const saleProceeds = Math.round(potentialSaleValue * (isDealerOrRentalCompany ? 0.935 : 0.9));

    // Preserve current checked states before updating
    const currentRentChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "rent")?.checked ?? true;
    const currentRpoChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "rpo")?.checked ?? false;
    const currentBuyItNowChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "buyItNow")?.checked ?? false;

    // Update checkbox cards without showing earnings information
    this.checkboxesForRentRtoSellItNow = [
      {
        value: "rent",
        label: "Rent",
        checked: currentRentChecked,
        body: "Rentals are offered on a weekly or monthly basis only, no daily rentals.",
      },
      {
        value: "rpo",
        label: "Rent To Own",
        checked: currentRentChecked && currentRpoChecked, // Only keep checked if rent is also checked
        disabled: !currentRentChecked, // Disable RPO if rent is not checked
        body: "Provide renters with a flexible purchase option at the end of the rental, allowing you to sell your machine at a retail price.",
      },
      {
        value: "buyItNow",
        label: "Sell It Now",
        checked: currentBuyItNowChecked,
        body: "List your machine for sale directly on the Rubbl marketplace.",
      },
    ];
  }

  private updateListingOptionsWithErrors(): void {
    // Preserve current checked states before updating
    const currentRentChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "rent")?.checked ?? true;
    const currentRpoChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "rpo")?.checked ?? false;
    const currentBuyItNowChecked = this.checkboxesForRentRtoSellItNow.find(card => card.value === "buyItNow")?.checked ?? false;

    // Update checkbox cards without showing error messages when we can't get earnings data
    this.checkboxesForRentRtoSellItNow = [
      {
        value: "rent",
        label: "Rent",
        checked: currentRentChecked,
        body: "Rentals are offered on a weekly or monthly basis only, no daily rentals.",
      },
      {
        value: "rpo",
        label: "Rent To Own",
        checked: currentRentChecked && currentRpoChecked, // Only keep checked if rent is also checked
        disabled: !currentRentChecked, // Disable RPO if rent is not checked
        body: "Provide renters with a flexible purchase option at the end of the rental, allowing you to sell your machine at a retail price.",
      },
      {
        value: "buyItNow",
        label: "Sell It Now",
        checked: currentBuyItNowChecked,
        body: "List your machine for sale directly on the Rubbl marketplace.",
      },
    ];
  }

  savePaymentMethod(): void {
    this.confirmedData.paymentMethod = this.paymentMethodId;
    this.nextConfirmationStep();
  }

  areAllTermsAccepted(): boolean {
    return this.terms.every(t => t.checked);
  }

  hasSuggestedRates(): boolean {
    return this.analysisResult && this.analysisResult.monthlyRate && this.analysisResult.monthlyRate > 0;
  }

  acceptSuggestedRates(): void {
    if (this.analysisResult) {
      // Apply 20% discount to OpenAI suggested rates
      this.confirmedData.rentalRates.monthly = Math.round(this.analysisResult.monthlyRate * 0.8).toString();
      this.confirmedData.rentalRates.weekly = this.analysisResult.weeklyRate ? Math.round(this.analysisResult.weeklyRate * 0.8).toString() : "";
      this.confirmedData.rentalRates.daily = this.analysisResult.dailyRate ? Math.round(this.analysisResult.dailyRate * 0.8).toString() : "";
    }
    this.nextConfirmationStep();
  }

  saveRentalRates(): void {
    // Rental rates are already bound to confirmedData.rentalRates
    this.nextConfirmationStep();
  }

  onCustomRentalRateChange(): void {
    // Auto-calculate weekly and monthly rates based on daily rate
    if (this.confirmedData.rentalRates.daily) {
      const dailyValue = parseFloat(this.confirmedData.rentalRates.daily.replace(/,/g, "")) || 0;

      // Calculate monthly from daily (daily * 30 / 0.15 for rental companies, daily * 30 / 0.14 for others)
      const isRentalCompany = this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer";
      const monthlyRate = isRentalCompany ? Math.round(dailyValue / 0.15) : Math.round(dailyValue / 0.14);
      const rates = this.inventoryService.calculateRentalRates(monthlyRate, this.account?.companyType);

      if (!this.confirmedData.rentalRates.weekly) {
        this.confirmedData.rentalRates.weekly = rates.weekly.toString();
      }
      if (!this.confirmedData.rentalRates.monthly) {
        this.confirmedData.rentalRates.monthly = monthlyRate.toString();
      }
    }
  }

  getCustomDailyEarnings(): number {
    if (!this.confirmedData.rentalRates.daily) return 0;
    const dailyRate = parseFloat(this.confirmedData.rentalRates.daily.replace(/,/g, "")) || 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? dailyRate * 0.8 : dailyRate * 0.7;
  }

  getCustomWeeklyEarnings(): number {
    if (!this.confirmedData.rentalRates.weekly) return 0;
    const weeklyRate = parseFloat(this.confirmedData.rentalRates.weekly.replace(/,/g, "")) || 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? weeklyRate * 0.8 : weeklyRate * 0.7;
  }

  getCustomMonthlyEarnings(): number {
    if (!this.confirmedData.rentalRates.monthly) return 0;
    const monthlyRate = parseFloat(this.confirmedData.rentalRates.monthly.replace(/,/g, "")) || 0;
    return this.account?.companyType === "RentalCompany" || this.account?.companyType === "Dealer" ? monthlyRate * 0.8 : monthlyRate * 0.7;
  }

  // Form field methods
  yearChange(year: string): void {
    this.entity.year = year;
  }

  onSerialNumberInput(event: any): void {
    event.target.value = event.target.value.toUpperCase();
  }

  isMileageUsage(): boolean {
    return this.meterKind() === "miles";
  }

  isNoneUsage(): boolean {
    return this.meterKind() === "none";
  }

  getUsageInputLabel(): string {
    return meterKindLabel(this.meterKind());
  }

  getUsageSummarySuffix(): string {
    return this.isMileageUsage() ? "miles" : "hours";
  }

  private meterKind(): MeterKind {
    const name = (this.currentUsageMeasurementName || "").toLowerCase();
    if (name === "none") {
      return "none";
    }

    if (name.includes("mile") || name.includes("kilometer")) {
      return "miles";
    }

    return "hours";
  }

  private applyUsageMeasurementByTypeDefinitionId(typeDefinitionId: string | null): void {
    if (!typeDefinitionId) {
      this.currentUsageMeasurementName = "Hours";
      this.syncEntityUsageUnitsFromCurrentMeasurement();
      return;
    }

    const label = this.machineTypes.find(type => type.value === typeDefinitionId)?.label;
    this.currentUsageMeasurementName = meterKindLabel(meterKindForNames(label));
    this.syncEntityUsageUnitsFromCurrentMeasurement();

    const cached = this.machineTypeUsageMeasurementById.get(typeDefinitionId);
    if (cached) {
      this.currentUsageMeasurementName = cached;
      this.syncEntityUsageUnitsFromCurrentMeasurement();
      return;
    }

    this.inventoryTypeService.get(typeDefinitionId).subscribe({
      next: def => {
        const type = def as MachineType;
        const kind = meterKindForNames(type?.name, type?.primaryType, (type as any)?.parentTypeDefinitionName, label);
        const resolved = meterKindLabel(kind);
        this.machineTypeUsageMeasurementById.set(typeDefinitionId, resolved);
        if (this.selectedMachineType !== typeDefinitionId) {
          return;
        }

        this.currentUsageMeasurementName = resolved;
        this.syncEntityUsageUnitsFromCurrentMeasurement();
      },
      error: () => {
        this.machineTypeUsageMeasurementById.delete(typeDefinitionId);
      },
    });
  }

  private syncEntityUsageUnitsFromCurrentMeasurement(): void {
    if (!this.entity?.usage?.standard?.unitOfMeasure || !this.entity?.usage?.metric?.unitOfMeasure) {
      return;
    }

    const kind = this.meterKind();
    const standard = standardMeterUnit(kind);
    const metric = metricMeterUnit(kind);
    this.entity.usage.standard.unitOfMeasure.name = standard.name;
    this.entity.usage.standard.unitOfMeasure.primaryAbbreviation = standard.primaryAbbreviation;
    this.entity.usage.metric.unitOfMeasure.name = metric.name;
    this.entity.usage.metric.unitOfMeasure.primaryAbbreviation = metric.primaryAbbreviation;
    this.syncHoursConfirmationStep();
  }

  private syncHoursConfirmationStep(): void {
    if (!this.confirmationSteps?.length) {
      return;
    }

    const index = this.confirmationSteps.indexOf("hours");
    if (this.isNoneUsage()) {
      if (index >= 0) {
        this.confirmationSteps.splice(index, 1);
        if (this.currentConfirmationStep > index) {
          this.currentConfirmationStep = Math.max(0, this.currentConfirmationStep - 1);
        }
      }
      return;
    }

    if (index >= 0) {
      return;
    }

    const yearIndex = this.confirmationSteps.findIndex(step => step === "year" || step === "year-manual");
    this.confirmationSteps.splice(yearIndex >= 0 ? yearIndex + 1 : this.confirmationSteps.length, 0, "hours");
  }

  onLocationCheckboxChange(checkboxCards: CheckboxCard[]): void {
    // Find which card was just checked
    const checkedCard = checkboxCards.find(card => card.checked);

    if (!checkedCard) {
      // No card selected
      this.entity.location = null;
      this.showLocationTypeahead = false;
      return;
    }

    // Ensure only one card is checked (radio button behavior)
    checkboxCards.forEach(card => {
      if (card !== checkedCard) {
        card.checked = false;
      }
    });

    // Update the checkbox cards array
    this.locationCheckboxCards = checkboxCards;

    if (checkedCard.value === "new") {
      // Show location typeahead for new address
      this.showLocationTypeahead = true;
      this.entity.location = null;
    } else {
      // Use existing yard location from the map
      this.showLocationTypeahead = false;
      const yard = this.locationYardMap.get(checkedCard.value);
      if (yard) {
        this.entity.location = yard;
      }
    }
  }

  locationSelected(location: string): void {
    this.entity.location = null;
    if (location === "new") {
      this.showLocationTypeahead = true;
      return;
    }

    this.showLocationTypeahead = false;
    const selectedYard = this.yardDropdownItems.find(d => d.label === location)?.value;
    if (selectedYard) {
      this.entity.location = selectedYard;
    }
  }

  locationTypeaheadSelected(location: Location): void {
    this.entity.location = location as any;
    this.showLocationTypeahead = true;
  }

  paymentMethodIdChange(paymentMethodId: string): void {
    this.paymentMethodId = paymentMethodId;
  }

  // Image analysis methods
  onImageFileSelected(file: File): void {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      this.toastrService.error("Please select a valid image file.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      this.toastrService.error("Image file must be less than 10MB.");
      return;
    }

    this.analyzingImage = true;
    this.analysisResult = null;
    this.showAnalysisResult = false;

    const machineTypeOptions: MachineTypeDefinitionOption[] = this.machineTypes.map(t => ({
      id: String(t.value),
      name: t.label,
    }));

    this.inventoryService.analyzeMachineImage(file, machineTypeOptions.length ? machineTypeOptions : undefined).subscribe({
      next: result => {
        this.analyzingImage = false;
        if (result && result.confidence > 30) {
          // Store AI results and start confirmation flow
          this.analysisResult = result;
          this.startConfirmationFlow();
        } else {
          this.toastrService.error("Unable to identify machine clearly. Please fill in details manually.");
          this.startConfirmationFlow(); // Start confirmation flow even without AI results
        }
      },
      error: error => {
        this.analyzingImage = false;
        this.toastrService.error("Failed to analyze image. Please try again or fill in details manually.");
        console.error("Image analysis error:", error);
        this.startConfirmationFlow(); // Start confirmation flow on error
      },
    });
  }

  skipImageUpload(): void {
    this.analysisResult = null;
    this.showAnalysisResult = false;
    // Start confirmation flow directly
    this.startConfirmationFlow();
  }

  startConfirmationFlow(): void {
    // Start the confirmation flow
    this.showAnalysisResult = false;
    this.showConfirmationSteps = true;
    this.currentConfirmationStep = 0;

    // Initialize confirmed data with AI results (if available)
    // Treat "unknown" (case-insensitive) as null
    const isUnknown = (value: string) => value && value.toLowerCase() === "unknown";

    this.confirmedData.type = this.analysisResult?.machineType && !isUnknown(this.analysisResult.machineType) ? this.analysisResult.machineType : null;
    if (this.analysisResult?.machineTypeDefinitionId) {
      const id = this.analysisResult.machineTypeDefinitionId;
      if (this.machineTypes.some(t => String(t.value) === id)) {
        this.selectedMachineType = id;
        this.applyUsageMeasurementByTypeDefinitionId(this.selectedMachineType);
      }
    }
    this.confirmedData.make = this.analysisResult?.make && !isUnknown(this.analysisResult.make) ? this.analysisResult.make : null;
    this.confirmedData.model = this.analysisResult?.model && !isUnknown(this.analysisResult.model) ? this.analysisResult.model : null;
    this.confirmedData.year = this.analysisResult?.year && !isUnknown(this.analysisResult.year) ? this.analysisResult.year : null;

    // Create ALL confirmation steps in order
    this.confirmationSteps = [];

    // Machine identification steps
    if (this.confirmedData.type) this.confirmationSteps.push("type");
    else this.confirmationSteps.push("type-manual");

    if (this.confirmedData.make) this.confirmationSteps.push("make");
    else this.confirmationSteps.push("make-manual");

    if (this.confirmedData.model) this.confirmationSteps.push("model");
    else this.confirmationSteps.push("model-manual");

    // Year and hours come before location so we can include them in AI request
    if (this.confirmedData.year) this.confirmationSteps.push("year");
    else this.confirmationSteps.push("year-manual");

    if (!this.isNoneUsage()) {
      this.confirmationSteps.push("hours");
    }

    // Location comes after year and hours so we have all data for AI request
    this.confirmationSteps.push("location");

    // Listing options come right after location (with enhanced earnings info based on year, hours, and location)
    this.confirmationSteps.push("listingOptions");

    // Serial number is the last step
    this.confirmationSteps.push("serialNumber");

    this.currentDropdownOptions = this.getDropdownOptions();
  }

  // Validation and navigation methods
  canProceed(): boolean {
    this.validationErrors = [];

    if (!this.selectedMachineType || !this.selectedMachineMake || !this.selectedMachineModel) {
      this.validationErrors.push("Please select machine type, make, and model.");
    }

    if (!this.entity.location) {
      this.validationErrors.push("Please select a machine location.");
    }

    if (!this.entity.year) {
      this.validationErrors.push("Please select a year.");
    }

    const reading = this.entity.usage?.standard?.value;
    if (!this.isNoneUsage() && (reading == null || reading < 0)) {
      this.validationErrors.push(`Please enter valid ${this.getUsageInputLabel().toLowerCase()}.`);
    }

    // Serial number is now optional - removed validation

    // Check if any rental rate is provided when rent is selected
    const hasRentalRate = this.entity.rentalRate || this.entity.rentalRateWeekly || this.entity.rentalRateDaily;
    if (this.isRentSelected && !hasRentalRate) {
      this.validationErrors.push("Please enter at least one rental rate.");
    }

    return this.validationErrors.length === 0;
  }

  // Create proper dimensions structure matching API expectations
  private createEmptyDimensions(): any {
    return {
      width: {
        standard: {
          value: 96,
          unitOfMeasure: {
            name: "Inches",
            system: "Standard",
            primaryAbbreviation: "in",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "mm",
          },
        },
        metric: {
          value: 2438,
          unitOfMeasure: {
            name: "Millimeters",
            system: "Metric",
            primaryAbbreviation: "mm",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "in",
          },
        },
      },
      height: {
        standard: {
          value: 100,
          unitOfMeasure: {
            name: "Inches",
            system: "Standard",
            primaryAbbreviation: "in",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "mm",
          },
        },
        metric: {
          value: 2540,
          unitOfMeasure: {
            name: "Millimeters",
            system: "Metric",
            primaryAbbreviation: "mm",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "in",
          },
        },
      },
      length: {
        standard: {
          value: 240,
          unitOfMeasure: {
            name: "Inches",
            system: "Standard",
            primaryAbbreviation: "in",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "mm",
          },
        },
        metric: {
          value: 6096,
          unitOfMeasure: {
            name: "Millimeters",
            system: "Metric",
            primaryAbbreviation: "mm",
            alternateAbbreviations: [],
            domain: "Length",
            alternateUnit: "in",
          },
        },
      },
      weight: {
        standard: {
          value: 25000,
          unitOfMeasure: {
            name: "Pounds",
            system: "Standard",
            primaryAbbreviation: "lbs",
            alternateAbbreviations: [],
            domain: "Mass",
            alternateUnit: "kg",
          },
        },
        metric: {
          value: 11340,
          unitOfMeasure: {
            name: "Kilograms",
            system: "Metric",
            primaryAbbreviation: "kg",
            alternateAbbreviations: [],
            domain: "Mass",
            alternateUnit: "lbs",
          },
        },
      },
      inventoryItemId: "00000000-0000-0000-0000-000000000000",
      accountId: "00000000-0000-0000-0000-000000000000",
      trucks: [],
      partitionKey: "00000000-0000-0000-0000-000000000000",
      id: null,
      isActive: true,
      itemType: ["ShippingDimensions"],
      ttl: -1,
      internalNotes: null,
    };
  }

  // New validation method for the create machine button in the review section

  canCreateMachine(): boolean {
    // Check if all terms are accepted
    if (!this.terms.every(d => d.checked)) {
      return false;
    }

    // Check payment method if required
    if (!this.account?.bypassCreditCard && !this.paymentMethodId) {
      return false;
    }

    return true;
  }

  onNext(): void {
    if (this.loading || !this.canProceed()) {
      return;
    }

    this.loading = true;

    // Create the machine
    this.createMachine();
  }

  onCancel(): void {
    this.router.navigate(["/owner/fleet"]);
  }

  private createMachine(): void {
    // Handle purchase price
    if (this.entity.purchasePrice == null || this.entity.purchasePrice <= 0) {
      this.entity.purchasePrice = -1;
    }

    // Handle location validation
    if (!this.entity.location || !this.entity.location.address) {
      this.toastrService.error("Please select or enter a valid location.");
      this.loading = false;
      return;
    }

    const catalogIdFromTypeScopedRows = this.findCatalogIdForSelectedMakeModel();
    const catalogLookup$ = catalogIdFromTypeScopedRows
      ? this.catalogService.get(catalogIdFromTypeScopedRows).pipe(
          catchError(err => {
            console.warn("Catalog get by type-scoped id failed; falling back to make/model search.", err);
            return this.catalogService.searchByMakeAndModel(this.selectedMachineMake, this.selectedMachineModel);
          }),
        )
      : this.catalogService.searchByMakeAndModel(this.selectedMachineMake, this.selectedMachineModel);

    const catalog$ = catalogLookup$.pipe(
      mergeMap(catalogItem => {
        // this endpoint always returns a catalog item, even if it creates it

        if (catalogItem.typeDefinitionCode == "REVIEW") {
          console.log("Catalog item is in REVIEW status, fetching specifications from OpenAI...");

          // Call OpenAI to get specifications
          return this.openAIService.getMachineSpecifications(this.selectedMachineMake, this.selectedMachineModel).pipe(
            mergeMap(specs => {
              if (specs && specs.length > 0) {
                console.log(`Received ${specs.length} specification categories from OpenAI`);

                // Transform grouped specifications into flat format for backend
                // Backend expects flat array with category field on each item
                const flattenedSpecs = this.flattenSpecifications(specs);
                catalogItem.specifications = flattenedSpecs;

                // Set the typeDefinitionId from the user's selection since we already know what type it is
                if (this.selectedMachineType) {
                  catalogItem.typeDefinitionId = this.selectedMachineType;
                  console.log(`Set typeDefinitionId to ${this.selectedMachineType} from user selection`);
                }

                // Extract weight information for shipping dimensions from the grouped specs
                const weightValue = this.extractWeightFromGroupedSpecs(specs);
                if (weightValue) {
                  catalogItem.shippingDimensions = catalogItem.shippingDimensions || this.createEmptyDimensions();
                  catalogItem.shippingDimensions.weight = catalogItem.shippingDimensions.weight || { standard: null, metric: null };

                  // Parse the weight value (e.g., "75845lbs") and set it
                  const weightMatch = weightValue.match(/^([\d.]+)(lbs?|kg)$/i);
                  if (weightMatch) {
                    const value = parseFloat(weightMatch[1]);
                    const unit = weightMatch[2].toLowerCase();

                    if (unit.startsWith("lb")) {
                      catalogItem.shippingDimensions.weight.standard = {
                        value: value,
                        unitOfMeasure: {
                          name: "Pounds",
                          system: "Standard",
                          primaryAbbreviation: "lbs",
                          domain: "Mass",
                        },
                      };
                      // Convert to kg for metric
                      catalogItem.shippingDimensions.weight.metric = {
                        value: Math.round(value * 0.453592),
                        unitOfMeasure: {
                          name: "Kilograms",
                          system: "Metric",
                          primaryAbbreviation: "kg",
                          domain: "Mass",
                        },
                      };
                    } else if (unit === "kg") {
                      catalogItem.shippingDimensions.weight.metric = {
                        value: value,
                        unitOfMeasure: {
                          name: "Kilograms",
                          system: "Metric",
                          primaryAbbreviation: "kg",
                          domain: "Mass",
                        },
                      };
                      // Convert to lbs for standard
                      catalogItem.shippingDimensions.weight.standard = {
                        value: Math.round(value / 0.453592),
                        unitOfMeasure: {
                          name: "Pounds",
                          system: "Standard",
                          primaryAbbreviation: "lbs",
                          domain: "Mass",
                        },
                      };
                    }
                  }
                }

                // Update the catalog item with new specifications, shipping dimensions, and typeDefinitionId
                return this.catalogService.update(catalogItem.id, catalogItem).pipe(
                  map(updatedCatalog => {
                    return catalogItem;
                  }),
                  catchError(updateError => {
                    console.error("Failed to update catalog item:", updateError);
                    // Return the original catalog item if update fails
                    return of(catalogItem);
                  }),
                );
              } else {
                console.warn("No specifications received from OpenAI");
                return of(catalogItem);
              }
            }),
            catchError(aiError => {
              console.error("Failed to get specifications from OpenAI:", aiError);
              // Return the original catalog item if AI call fails
              return of(catalogItem);
            }),
          );
        }

        return of(catalogItem);
      }),
      catchError(error => {
        console.error("Failed to get catalog information:", error);
        return EMPTY;
      }),
    );

    // Since location is already created, we just need catalog info
    catalog$
      .pipe(
        mergeMap((catalog: MachineCatalog) => {
          this.entity.catalogItemId = catalog.id;
          this.entity.make = catalog.make;
          this.entity.model = catalog.model;
          this.entity.specifications = catalog.specifications;
          this.entity.typeDefinitionId = catalog.typeDefinitionId;
          this.entity.displayName = catalog.displayName;
          this.entity.lastUpdatedByOwnerDateTime = new Date().toISOString();

          return this.inventoryService.create({
            item: this.entity,
            dimensions: catalog.shippingDimensions || this.createEmptyDimensions(),
          });
        }),
        tap(result => {
          this.logService.trackEvent("machine-create-wizard-successful");
          this.toastrService.success("Machine Created!", null, {
            positionClass: "toast-top-center",
          });

          this.machineCreated.emit(result);
          this.loading = false;

          // Navigate to wizard pricing with machine ID in route
          this.router.navigate(["/owner/fleet/wizard", result.id, "pricing"], {
            queryParams: {
              partitionKey: this.entity.accountId,
            },
          });
        }),
        catchError(err => {
          this.logService.trackEvent("machine-create-wizard-error");
          this.logService.error("machine-create-wizard", err);

          if (err.status === 409) {
            this.toastrService.error("The selected location already exists. Please choose a different location.");
          } else {
            this.toastrService.error("Unexpected error occurred. Please contact support.");
          }
          this.loading = false;
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * Flatten grouped specifications into flat array format for backend
   * Transforms grouped format (category with items) into flat format with category field
   * @param groupedSpecs - Array of specification categories with items
   * @returns Flattened array of specifications
   */
  private flattenSpecifications(groupedSpecs: any[]): any[] {
    const flattenedSpecs = [];

    for (const category of groupedSpecs) {
      if (!category.items || !Array.isArray(category.items)) {
        continue;
      }

      for (const item of category.items) {
        // Transform each item into the backend's expected format
        const flatSpec = {
          name: item.name,
          category: category.name,
          value: item.value || "",
          displayOnCard: item.displayOnCard !== undefined ? item.displayOnCard : false,
          specificationType: "Description", // Default to Description type
          isRequired: false,
          isTypeDefinitionSpec: false,
          isPublic: true,
          isPrimary: false,
          modified: false,
          replaced: false,
          standardMeasurement: null,
          metricMeasurement: null,
          allowedValues: [],
        };

        flattenedSpecs.push(flatSpec);
      }
    }

    console.log(`Flattened ${flattenedSpecs.length} specifications from ${groupedSpecs.length} categories`);
    return flattenedSpecs;
  }

  /**
   * Extract weight value from grouped specifications array
   * Looks for "Operating Weight", "Net Weight", or "Total Operating Weight" in any category
   * @param specs - Array of specification categories
   * @returns Weight value string (e.g., "75845lbs") or null if not found
   */
  private extractWeightFromGroupedSpecs(specs: any[]): string | null {
    if (!specs || specs.length === 0) {
      return null;
    }

    // Search through all categories for weight-related items
    for (const category of specs) {
      if (!category.items || !Array.isArray(category.items)) {
        continue;
      }

      // Look for weight specifications (various possible names)
      const weightItem = category.items.find(
        item =>
          item.name &&
          (item.name.toLowerCase().includes("operating weight") || item.name.toLowerCase().includes("net weight") || item.name.toLowerCase() === "weight"),
      );

      if (weightItem && weightItem.value) {
        console.log(`Found weight specification: ${weightItem.name} = ${weightItem.value}`);
        return weightItem.value;
      }
    }

    return null;
  }
}
